import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';

import { VehicleImageClient } from '../../client/vehicle-image.client';

@Injectable({
  providedIn: 'root',
})
export class VehicleImageService {
  private readonly vehicleImageClient = inject(VehicleImageClient);
  private readonly logger = inject(NGXLogger);

  /**
   * Cache for vehicle images, keyed by VIN. The promise resolves to the image URL.
   *
   * This cache stored in memory and not evicted, so it lasts until the page is closed/reloaded.
   * Since it only stores URLs (which are small) and not the images themselves, this should not lead to memory issues.
   * The actual image data is managed by the browser's caching mechanisms when the URL is used in an `<img>` tag.
   *
   * The cache contains promises
   * to ensure that if multiple requests for the same VIN are made before the first one completes,
   * they will all receive the same promise and thus the same result,
   * rather than triggering multiple HTTP requests for the same image.
   *
   * If a request for an image fails (e.g. due to a network error), the failed promise is removed from the cache,
   * allowing future requests for the same VIN to retry fetching the image.
   * If a request fails with a 404 error, the promise resolves to null and remains in the cache,
   * indicating that there is no image for that VIN without needing to retry the request.
   */
  private readonly vinImageCache = new Map<string, Promise<string | null>>();

  getVehicleImageUrl(vin: string): Promise<string | null> {
    const cachedImageUrlPromise = this.vinImageCache.get(vin);
    if (cachedImageUrlPromise) {
      return cachedImageUrlPromise;
    }

    const imageUrlPromise = this.fetchVehicleImageUrl(vin).catch((e: unknown) => {
      if (e instanceof HttpErrorResponse && e.status === 404) {
        // If the image is not found, the result is null.
        this.logger.warn('#CDFFF0238 No image found for VIN', vin);
        return null;
      }

      // If the request fails, discard this from the cache so that future requests can retry fetching the image.
      if (this.vinImageCache.get(vin) === imageUrlPromise) {
        this.logger.error('#CDFTF0239 Request to fetch the image for VIN has failed, discarding the cache', vin, e);
        this.vinImageCache.delete(vin);
      } else {
        this.logger.error(
          '#CDFFF0240 Request to fetch the VIN has failed, but the cache has already been updated with a new request, not modifying the cache',
          vin,
          e,
        );
      }
      throw e;
    });
    this.vinImageCache.set(vin, imageUrlPromise);
    return imageUrlPromise;
  }

  private async fetchVehicleImageUrl(vin: string): Promise<string | null> {
    const response = await this.vehicleImageClient.getVehicleImageUrl(vin);
    if (!response.imageUrl) {
      this.logger.warn('#CDFTF0242 No image URL found in the response for VIN', vin);
      throw new Error(`No image URL found in the response for VIN ${vin}`);
    }
    this.logger.info('#CDFFF0241 Successfully fetched image URL for VIN', vin);
    return response.imageUrl;
  }
}
